import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';
import './AboutPage.css';

const AboutPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return false; // default to light for about page
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Check for dark mode preference using the app's custom system
    const checkDarkMode = () => {
      const storedTheme = localStorage.getItem("theme");
      const isDark = storedTheme ? storedTheme === "dark" : false; // default to light for about page
      setIsDarkMode(isDark);
      setDarkMode(isDark);
    };

    // Initial check
    checkDarkMode();

    // Listen for changes to localStorage (when user toggles theme in navbar)
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        checkDarkMode();
      }
    };

    // Listen for changes to data-bs-theme attribute
    const observer = new MutationObserver(() => {
      checkDarkMode();
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-bs-theme']
    });

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorageChange);
      observer.disconnect();
    };
  }, [scrolled]);

  // Theme toggle handler
  const handleThemeToggle = () => {
    setDarkMode(prev => !prev);
  };

  // Handle theme changes
  useEffect(() => {
    document.body.setAttribute("data-bs-theme", darkMode ? "dark" : "light");
    localStorage.setItem("theme", darkMode ? "dark" : "light");
    setIsDarkMode(darkMode);
  }, [darkMode]);

  const navbarShouldBeScrolled = scrolled || navbarExpanded;

  return (
    <div className="about-page">
      <PageTitle title="About Us" description="Learn more about MedHive and our mission to revolutionize medical education" />
      {/* Navigation Bar */}
      <nav className={`navbar custom-navbar navbar-expand-lg navbar-light fixed-top${navbarShouldBeScrolled ? ' scrolled' : ''}`}
        style={{ zIndex: 1050 }}
      >
        <div className="container">
          <Link className="navbar-brand fw-bold d-flex align-items-center" to="/">
            <img src={isDarkMode ? "/images/asset4.svg" : "/images/asset5.svg"} alt="brand logo" style={{ height: "16px"}}/>

            {/*<img
              src={navbarShouldBeScrolled ? "/images/logo5.svg" : "/images/logo4.svg"}
              alt="brand logo"
              style={{ width: "32px", height: "32px", marginTop: "-4px" }}
            />
            <span className="ms-2">medhive</span>*/}
          </Link>
          <div className="d-flex align-items-center ms-auto order-lg-2">
            <button
              className="btn btn-light-lite px-3"
              onClick={handleThemeToggle}
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {darkMode ? (
                <i className="bi bi-sun-fill"></i>
              ) : (
                <i className="bi bi-moon-fill text-dark"></i>
              )}
            </button>
            <Link to="/login" className="btn btn-sm btn-primary ms-2">Login</Link>
            <button
              className="navbar-toggler btn-sm ms-2"
              type="button"
              aria-controls="mainNavbar"
              aria-expanded={navbarExpanded}
              aria-label="Toggle navigation"
              onClick={() => setNavbarExpanded((prev) => !prev)}
            >
              <span><i className={navbarShouldBeScrolled ? "bi bi-list" : "bi bi-list opacity-50"}></i></span> 
            </button>
          </div>
          <div className={`collapse navbar-collapse justify-content-center order-lg-1${navbarExpanded ? ' show' : ''}`} id="mainNavbar">
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0 gap-lg-4 gap-2 align-items-lg-center text-center">
              <li className="nav-item">
                <Link to="/" className="nav-link" onClick={() => setNavbarExpanded(false)}>Home</Link>
              </li>
              <li className="nav-item">
                <a href="#features" className="nav-link" onClick={e => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); setNavbarExpanded(false); }}>Features</a>
              </li>
              <li className="nav-item">
                <a href="#pricing" className="nav-link" onClick={e => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); setNavbarExpanded(false); }}>Pricing</a>
              </li>
              <li className="nav-item">
                <Link to="/about" className="nav-link" onClick={() => setNavbarExpanded(false)}>About Us</Link>
              </li>
              <li className="nav-item">
                <Link to="/contact" className="nav-link" onClick={() => setNavbarExpanded(false)}>Contact Us</Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <section className="about-header-section">
        {/* Hero Background Elements */}
        <div className="hero-background-elements">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-gradient-orb orb-3"></div>
        </div>
        <div className="container">
          <div className="row align-items-center" style={{ minHeight: '220px' }}>
            <div className="col-lg-12 text-center">
              <h1 className="display-4 fw-bold mb-3 gradient-text">About medhive</h1>
              <p className="lead text-muted">Revolutionizing medical education through innovative technology solutions for rotation management, evaluation, and compliance tracking.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-6">
              <div className="mission-content">
                <h2 className="gradient-text mb-4">Our Mission</h2>
                <p className="lead mb-4">
                  To transform medical education by providing comprehensive, user-friendly 
                  solutions that enhance the learning experience for residents, streamline 
                  administrative processes for program directors, and ensure compliance 
                  with accreditation standards.
                </p>
                <div className="d-flex flex-row justify-content-start mission-stats">
                  <div className="stat-item">
                    <h3>----</h3>
                    <p>Active Users</p>
                  </div>
                  <div className="stat-item">
                    <h3>----</h3>
                    <p>Partner Hospitals</p>
                  </div>
                  <div className="stat-item">
                    <h3>----</h3>
                    <p>Satisfaction Rate</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="mission-image d-flex justify-content-center align-items-center" style={{ minHeight: '220px' }}>
                <div className="innovation-illustration">
                  <div className="innovation-container">
                  {/* Main Lightbulb in clipped/shadowed container */}
                  <div className="innovation-lightbulb-container">
                    <i className="bi bi-lightbulb"></i>
                  </div></div>
                  {/* Orbiting Icons with backgrounds */}
                  {/* Top Left - Lightning */}
                  <div className="innovation-orbit-icon innovation-orbit-top-left">
                    <div className="innovation-orbit-bg innovation-orbit-bg-orange">
                      <i className="bi bi-lightning-charge-fill"></i>
                    </div>
                  </div>
                  <div className="innovation-orbit-icon innovation-orbit-top-middle">
                    <div className="innovation-orbit-bg2 innovation-orbit-bg-blue">
                      <i className="bi bi-file-earmark-medical-fill"></i>
                    </div>
                  </div>
                  {/* Top Right - Gear */}
                  <div className="innovation-orbit-icon innovation-orbit-top-right">
                    <div className="innovation-orbit-bg innovation-orbit-bg-purple">
                      <i className="bi bi-folder-fill"></i>
                    </div>
                  </div>
                  
                  {/* Bottom Left - Stars */}
                  <div className="innovation-orbit-icon innovation-orbit-bottom-left">
                    <div className="innovation-orbit-bg innovation-orbit-bg-green2">
                      <i className="bi bi-people"></i>
                    </div>
                  </div>
                  <div className="innovation-orbit-icon innovation-orbit-left-middle">
                    <div className="innovation-orbit-bg2 innovation-orbit-bg-green">
                      <i className="bi bi-calendar"></i>
                    </div>
                  </div>
                  {/* Bottom Right - Lightning */}
                  <div className="innovation-orbit-icon innovation-orbit-bottom-right">
                    <div className="innovation-orbit-bg innovation-orbit-bg-orange">
                      <i className="bi bi-stars"></i>
                    </div>
                  </div>
                  <div className="innovation-orbit-icon innovation-orbit-bottom-right2">
                    <div className="innovation-orbit-bg2 innovation-orbit-bg-pink">
                      <i className="bi bi-stars"></i>
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2>Our Core Values</h2>
            <div className="text-hl-home"></div>
            <p className="text-muted mt-3">The principles that guide everything we do</p>
          </div>
          <div className="row g-4">
            <div className="col-lg-4 col-md-6">
              <div className="value-card">
                <div className="value-icon" style={{background: 'linear-gradient(135deg, #007bff 0%, #00c6ff 100%)'}}>
                  <i className="bi bi-shield-check"></i>
                </div>
                <h4>Security & Compliance</h4>
                <p>We prioritize the security of sensitive medical data and ensure full compliance with healthcare regulations including HIPAA and ACGME standards.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="value-card">
                <div className="value-icon" style={{background: 'linear-gradient(135deg, #28a745 0%, #a8e063 100%)'}}>
                  <i className="bi bi-people"></i>
                </div>
                <h4>User-Centric Design</h4>
                <p>Every feature is designed with the end user in mind, ensuring intuitive navigation and efficient workflows for all stakeholders.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="value-card">
                <div className="value-icon" style={{background: 'linear-gradient(135deg, #6f42c1 0%, #b993d6 100%)'}}>
                  <i className="bi bi-graph-up"></i>
                </div>
                <h4>Continuous Improvement</h4>
                <p>We constantly evolve our platform based on user feedback and industry best practices to deliver the best possible experience.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="value-card">
                <div className="value-icon" style={{background: 'linear-gradient(135deg, #fd7e14 0%, #ffb347 100%)'}}>
                  <i className="bi bi-handshake"></i>
                </div>
                <h4>Partnership</h4>
                <p>We believe in building long-term partnerships with healthcare institutions, working together to improve medical education outcomes.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="value-card">
                <div className="value-icon" style={{background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)'}}>
                  <i className="bi bi-lightning"></i>
                </div>
                <h4>Innovation</h4>
                <p>We embrace cutting-edge technology to solve complex challenges in medical education and training program management.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="value-card">
                <div className="value-icon" style={{background: 'linear-gradient(135deg, #e83e8c 0%, #ffb6b9 100%)'}}>
                  <i className="bi bi-heart"></i>
                </div>
                <h4>Excellence</h4>
                <p>We are committed to excellence in everything we do, from product development to customer support and service delivery.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2>Our Leadership Team</h2>
            <div className="text-hl-home"></div>
            <p className="text-muted mt-3">Meet the experts behind medhive</p>
          </div>
          <div className="row g-4">
            <div className="col-lg-4 col-md-6">
              <div className="team-card bg-light-card-2">
                <div className="team-image">
                  <img src="/images/Ceo_pic.png" alt="CEO" />
                </div>
                <div className="team-info">
                  <h4>Dr. Al-Murtadha Al-Gahmi</h4>
                  <p className="team-title">CEO & Co-Founder</p>
                  <p className="team-bio">
                    Proin id odio vel purus blandit viverra. Sed varius gravida ligula vel vulputate. Interdum et malesuada fames ac ante ipsum primis in faucibus. Donec tristique non libero at ultricies. Aenean ultricies blandit ex, eget viverra urna malesuada a. Mauris sed justo libero. Etiam sit amet nibh at nisi hendrerit pellentesque a in erat. Suspendisse non dolor id ex venenatis luctus hendrerit et tellus.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="team-card bg-light-card-2">
                <div className="team-image">
                  <img src="/images/Cfo_pic.png" alt="CTO" />
                </div>
                <div className="team-info">
                  <h4>Jane Doe Smith</h4>
                  <p className="team-title">CTO & Co-Founder</p>
                  <p className="team-bio">
                  Phasellus eget augue commodo, luctus augue vel, finibus arcu. Ut vitae aliquam diam. Aliquam eu ultricies nibh, sed ullamcorper nulla. Ut ipsum diam, accumsan eu ex id, placerat ornare eros. Duis mattis ut tellus in suscipit. Sed consectetur volutpat dolor at luctus. Proin at massa risus. Mauris volutpat ex id velit suscipit, nec ultrices justo bibendum. Donec ac velit et neque luctus fringilla.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="team-card bg-light-card-2">
                <div className="team-image">
                  <img src="/images/Coo_pic.png" alt="COO" />
                </div>
                <div className="team-info">
                  <h4>Dr. Zahra Arbabi</h4>
                  <p className="team-title">COO</p>
                  <p className="team-bio">
                  Mauris aliquam tincidunt mauris et porttitor. Phasellus lobortis lacus metus, non eleifend elit ornare id. Morbi a ultricies augue, vitae pulvinar est. Aliquam ut mi venenatis lectus pretium commodo. Nam faucibus felis eget nulla egestas eleifend.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="row g-5 justify-content-center">
            <div className="col-lg-8 mx-auto">
              <div className="story-content text-center mb-5">
                <h2 className="gradient-text mb-4">Our Story</h2>
                <p className="lead mb-4">
                  medhive was born from a simple observation: medical education programs 
                  were struggling with outdated, fragmented systems that made it difficult 
                  to manage rotations, track evaluations, and ensure compliance.
                </p>
                <p className="mb-4">
                  Founded by a team of healthcare professionals and technology experts, 
                  we set out to create a comprehensive platform that would streamline 
                  every aspect of medical education management. Our journey began with 
                  a deep understanding of the challenges faced by program directors, 
                  residents, and attending physicians.
                </p>
                <p>
                  Today, medhive plans to serve hundreds of healthcare institutions across the 
                  country, helping them improve their educational programs while reducing 
                  administrative burden and ensuring compliance with accreditation standards.
                </p>
              </div>
              {/* Minimal Modern Horizontal Timeline */}
              <div className="story-timeline-horizontal minimal mt-4">
                <div className="timeline-horizontal-track">
                  <div className="timeline-horizontal-items d-flex flex-row flex-nowrap justify-content-between align-items-start minimal">
                    {/* 2024 */}
                    <div className="timeline-horizontal-item text-center flex-fill minimal">
                      <div className="timeline-horizontal-mark-container minimal mx-auto mb-2">
                        <div className="timeline-horizontal-mark minimal bg-primary mx-auto"></div>
                        <div className="timeline-horizontal-year minimal">2024</div>
                      </div>
                      <div className="timeline-horizontal-content minimal mx-auto">
                        <div className="fw-semibold mb-1">Foundation</div>
                        <div className="mb-0 text-muted small">medhive was founded with a vision to transform medical education technology</div>
                      </div>
                    </div>
                    {/* 2025 */}
                    <div className="timeline-horizontal-item text-center flex-fill minimal">
                      <div className="timeline-horizontal-mark-container minimal mx-auto mb-2">
                        <div className="timeline-horizontal-mark minimal bg-success mx-auto"></div>
                        <div className="timeline-horizontal-year minimal">2025</div>
            </div>
                      <div className="timeline-horizontal-content minimal mx-auto">
                        <div className="fw-semibold mb-1">First Launch</div>
                        <div className="mb-0 text-muted small">Released our first version with rotation scheduling and basic evaluation features</div>
                  </div>
                </div>
                    {/* 2026 */}
                    <div className="timeline-horizontal-item text-center flex-fill minimal">
                      <div className="timeline-horizontal-mark-container minimal mx-auto mb-2">
                        <div className="timeline-horizontal-mark minimal bg-warning mx-auto"></div>
                        <div className="timeline-horizontal-year minimal">2026</div>
                      </div>
                      <div className="timeline-horizontal-content minimal mx-auto">
                        <div className="fw-semibold mb-1">Growth</div>
                        <div className="mb-0 text-muted small">Aim to expand and serve 100+ healthcare institutions nationwide, fostering collaboration and innovation.</div>
                  </div>
                </div>
                    {/* 2027 */}
                    <div className="timeline-horizontal-item text-center flex-fill minimal">
                      <div className="timeline-horizontal-mark-container minimal mx-auto mb-2">
                        <div className="timeline-horizontal-mark minimal bg-info mx-auto"></div>
                        <div className="timeline-horizontal-year minimal">2027</div>
                      </div>
                      <div className="timeline-horizontal-content minimal mx-auto">
                        <div className="fw-semibold mb-1">Innovation</div>
                        <div className="mb-0 text-muted small">Plan to launch advanced features such as AI-powered scheduling, predictive analytics, and seamless integrations.</div>
                  </div>
                </div>
                    {/* 2028 */}
                    <div className="timeline-horizontal-item text-center flex-fill minimal">
                      <div className="timeline-horizontal-mark-container minimal mx-auto mb-2">
                        <div className="timeline-horizontal-mark minimal bg-danger mx-auto"></div>
                        <div className="timeline-horizontal-year minimal">2028</div>
                      </div>
                      <div className="timeline-horizontal-content minimal mx-auto">
                        <div className="fw-semibold mb-1">Leadership</div>
                        <div className="mb-0 text-muted small">Strive to become the leading platform for medical education management, empowering 5000+ active users globally.</div>
                  </div>
                </div>
                  </div>
                  {/* Minimal horizontal line under marks */}
                  <div className="timeline-horizontal-line minimal position-absolute w-100" style={{top: 0, left: 0, height: 2, background: '#e0e0e0', zIndex: 10000}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="container text-center">
          <h2 className="gradient-text mb-4">Join the Future of Medical Education</h2>
          <p className="lead mb-4">Be part of the transformation in healthcare education technology</p>
          <div className="d-flex gap-3 justify-content-center">
            <Link to="/contact" className="btn btn-light btn-lg px-5 py-3">Contact Us</Link>
            <Link to="/signup" className="btn btn-outline-light btn-lg px-5 py-3">Get Started</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage; 